import './index.css'
import clsx from 'clsx'

import React, { useState } from 'react'
import FileSelect from 'components/common/file-select'
import Button from 'components/ui/button'
import ErrorRow from 'components/ui/error-row'
import Icon, { IconKey } from 'components/ui/icon'
import FileDropWrapper from 'containers/FileDropWrapper'
type Props = {
  onFileDrop: (...args: Array<any>) => any
  onClear: (...args: Array<any>) => any
  errors: (string | null | undefined)[]
  files?: (File | null | undefined)[]
  loading?: boolean
  successMessage?: string
  dropZoneIcon?: {
    type: IconKey
    color?: string
  }
  fileIcon?: {
    type: IconKey
    color?: string
  }
  multiple?: boolean
}

/**
 * Drag and drop file upload component with visual feedback
 * Supports both drag-and-drop and click-to-browse file selection
 * @param onFileDrop - Callback when files are dropped or selected
 * @param onClear - Callback to clear files or handle errors
 * @param errors - Array of error messages to display
 * @param files - Array of selected files to display
 * @param loading - Whether upload is in progress
 * @param dropZoneIcon - Icon configuration for the drop zone
 * @param fileIcon - Icon configuration for file items
 * @param multiple - Whether to allow multiple file selection
 */
const DragAndDrop = ({
  onFileDrop,
  onClear,
  errors: originErrors,
  files: originFiles,
  loading,
  dropZoneIcon,
  fileIcon,
  multiple,
}: Props): React.ReactElement<'div'> => {
  const files = (originFiles || []).filter(Boolean)
  const [isOver, setIsOver] = useState(false)
  const errors = originErrors.filter(Boolean)

  const onOver = newOver => {
    if (newOver !== isOver) {
      setIsOver(newOver)
    }
  }

  const ready = files.length > 0 && errors.length === 0 && !loading
  return (
    <div className="DragAndDrop">
      <div
        className={clsx('DragAndDrop_Dropzone', {
          'DragAndDrop_Dropzone--Error': errors.length > 0,
          'DragAndDrop_Dropzone--Ready': ready,
          'DragAndDrop_Dropzone--Over': isOver,
        })}
      >
        <FileDropWrapper
          multiple={multiple}
          onDrop={onFileDrop}
          onOver={onOver}
        >
          <FileSelect
            className="DragAndDrop_FileSelect"
            multiple={multiple}
            onChange={onFileDrop}
          >
            <div>
              {dropZoneIcon && (
                <div className="DragAndDrop_FileSelectIcon">
                  <Icon
                    type={dropZoneIcon.type}
                    color={dropZoneIcon.color}
                    onClick={onClear}
                  />
                </div>
              )}
              <span>
                {isOver
                  ? `Drop ${multiple ? 'files' : 'file'} to upload`
                  : `Drag ${
                      multiple ? 'files' : 'file'
                    } here, or click to browse`}
              </span>
            </div>
          </FileSelect>
        </FileDropWrapper>
      </div>
      {errors.map(text => (
        <ErrorRow key={text} text={text} />
      ))}
      {files.map((file, index) => (
        <div className="DragAndDrop_FileRow" key={`${file.name}${index}`}>
          <div className="DragAndDrop_IconWrapper overflow-ellipsis">
            {fileIcon && (
              <div className="DragAndDrop_IconContainer">
                <Icon type={fileIcon.type} color={fileIcon.color} />
              </div>
            )}
            <div className="overflow-ellipsis">{file?.name}</div>
          </div>

          <div className="DragAndDrop_ClearButton">
            <Button icon="close" onClick={event => onClear(event, file)} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default DragAndDrop
